import { type ApolloQueryResult, useApolloClient } from '@apollo/client';
import { Button, Col, Row, message } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ResetFinderVehicleDocument,
    type ResetFinderVehicleMutation,
    type ResetFinderVehicleMutationVariables,
} from '../../../api/mutations/resetFinderVehicle';
import { type GetVehicleQuery, type GetVehicleQueryVariables } from '../../../api/queries/getVehicle';
import { FinderVehicleStatus } from '../../../api/types';
import RadioGroupField from '../../../components/fields/RadioGroupField';
import FormFields from '../../../themes/admin/Fields/FormFields';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useTooltip from '../../../utilities/useTooltip';
import useTranslatedString from '../../../utilities/useTranslatedString';
import useFinderOptions from '../FinderVehicleListPage/useFinderOptions';
import { type FinderVehicleFormValues, colSpan } from './shared';

const LinkButton = styled(Button)`
    padding: 0;
    margin-left: 0.5rem;

    & span {
        text-decoration: underline;
    }
`;

type MainDetailsProps = {
    disabled?: boolean;
    refetch: (variables?: Partial<GetVehicleQueryVariables>) => Promise<ApolloQueryResult<GetVehicleQuery>>;
    hasLTA: boolean;
};

const MainDetails = ({ disabled = false, refetch, hasLTA }: MainDetailsProps) => {
    const { t } = useTranslation('finderVehicle');

    const { isStateHidden, functionality } = useFinderOptions();
    const { values } = useFormikContext<FinderVehicleFormValues>();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: values.versioning,
        moduleId: values.moduleId,
    });

    const translatedString = useTranslatedString();
    const tooltip = useTooltip('finderButton');

    const apolloClient = useApolloClient();

    const financeProductOptions = useMemo(() => {
        if (!values.financeProducts.length) {
            return [];
        }

        return values.financeProducts.map(({ versioning, legalName }) => ({
            value: versioning.suiteId,
            label: translatedString(legalName),
        }));
    }, [translatedString, values.financeProducts]);

    const canReset = [FinderVehicleStatus.Reserved, FinderVehicleStatus.Submitted].includes(values.status);

    const handleReset = useCallback(async () => {
        message.loading({
            content: t('finderVehicle:messages.resetSubmitting'),
            key: 'primary',
            duration: 0,
        });

        const variables = {
            id: values.id,
        };

        await apolloClient
            .mutate<ResetFinderVehicleMutation, ResetFinderVehicleMutationVariables>({
                mutation: ResetFinderVehicleDocument,
                variables,
            })
            .then(() => {
                refetch({ id: values.id });
            })
            .finally(() => {
                message.destroy('primary');
            });

        // inform about success
        message.success({
            content: t('finderVehicle:messages.updateSuccessful'),
            key: 'primary',
        });
    }, [apolloClient, refetch, t, values.id]);

    const statusLabel = useMemo(
        () => (
            <span>
                {t('finderVehicle:fields.status.label')}
                {!disabled && (
                    <LinkButton disabled={!canReset} onClick={handleReset} type="link">
                        {t('finderVehicle:actions.reset')}
                    </LinkButton>
                )}
            </span>
        ),
        [canReset, disabled, handleReset, t]
    );

    return (
        <Row gutter={14}>
            <Col {...colSpan}>
                <RadioGroupField
                    {...t('finderVehicle:fields.buttonState', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="setting.isStateHidden"
                    options={isStateHidden}
                    tooltip={tooltip}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <RadioGroupField
                    {...t('finderVehicle:fields.functionality', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="setting.functionality"
                    options={functionality}
                    tooltip={tooltip}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <RadioGroupField
                    {...t('finderVehicle:fields.isTestDriveButtonHidden', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="setting.isTestDriveButtonHidden"
                    options={isStateHidden}
                    tooltip={tooltip}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.DisplayField label={statusLabel} value={values.status} />
            </Col>
            <Col {...colSpan}>
                <FormFields.SelectField
                    {...t('finderVehicle:fields.financeProducts', {
                        returnObjects: true,
                    })}
                    mode="multiple"
                    name="financeProductSuiteIds"
                    options={financeProductOptions}
                    disabled
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.DisplayFieldWithUTCOffset
                    {...t('finderVehicle:fields.updatedAt', { returnObjects: true, offset })}
                    value={updated}
                />
            </Col>
        </Row>
    );
};

export default MainDetails;
